import React from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import twoBedroom from "../assets/ss1.png";
import threeBedroom from "../assets/ss2.png";
import penthouse from "../assets/ss3.png";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const ApartmentsSection = styled.section`
  padding: 120px 0;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, #1a5f7a, #159895);
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, #159895, #1a5f7a);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  font-size: 3.2rem;
  color: #1a5f7a;
  text-align: center;
  margin-bottom: 20px;
  font-weight: 800;
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease-out forwards;
  background: linear-gradient(135deg, #1a5f7a, #159895);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.3rem;
  color: #666;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 80px;
  line-height: 1.8;
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease-out 0.2s forwards;
`;

const ApartmentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  margin-top: 50px;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const ApartmentCard = styled.div`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeInUp} 0.8s ease-out ${(props) => props.delay}s forwards;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #1a5f7a, #159895);
    transform: scaleX(0);
    transition: transform 0.4s ease;
  }

  &:hover {
    transform: translateY(-15px);
    box-shadow: 0 20px 45px rgba(0, 0, 0, 0.1);

    &::before {
      transform: scaleX(1);
    }

    img {
      transform: scale(1.1);
    }
  }
`;

const ApartmentImage = styled.div`
  height: 280px;
  overflow: hidden;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.3), transparent);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s ease;
  }
`;

const ApartmentContent = styled.div`
  padding: 35px;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;
`;

const ApartmentTitle = styled.h3`
  font-size: 1.8rem;
  color: #1a5f7a;
  margin-bottom: 20px;
  font-weight: 700;
`;

const ApartmentDescription = styled.p`
  color: #666;
  line-height: 1.8;
  margin-bottom: 25px;
  font-size: 1.1rem;
  flex: 1;
`;

const ApartmentFeatures = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  flex-wrap: nowrap;
  justify-content: space-between;
  overflow-x: auto;
  padding-bottom: 10px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }

  @media (max-width: 576px) {
    gap: 8px;
  }
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #666;
  font-size: 1rem;
  background: #f8f9fa;
  padding: 8px 12px;
  border-radius: 50px;
  transition: all 0.3s ease;
  flex: 0 0 auto;
  white-space: nowrap;
  min-width: fit-content;

  &:hover {
    background: #e9ecef;
    transform: translateY(-2px);
  }

  span {
    font-weight: 600;
    color: #1a5f7a;
  }

  @media (max-width: 576px) {
    font-size: 0.9rem;
    padding: 6px 10px;
  }
`;

const ViewButton = styled(Link)`
  display: inline-block;
  padding: 15px 35px;
  background: linear-gradient(135deg, #1a5f7a, #159895);
  color: white;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.4s ease;
  text-align: center;
  width: 100%;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(26, 95, 122, 0.2);
    background: linear-gradient(135deg, #159895, #1a5f7a);
  }
`;

const Apartments = () => {
  return (
    <ApartmentsSection id="apartments">
      <Container>
        <SectionTitle>Our Apartments</SectionTitle>
        <SectionSubtitle>
          Discover our range of luxurious apartments designed for modern living
        </SectionSubtitle>

        <ApartmentGrid>
          <ApartmentCard delay={0.3}>
            <ApartmentImage>
              <img src={twoBedroom} alt="2 Bedroom Apartment" />
            </ApartmentImage>
            <ApartmentContent>
              <ApartmentTitle>2 Bedroom Apartment</ApartmentTitle>
              <ApartmentDescription>
                Perfect for families, our spacious 2-bedroom apartments feature
                a modern open-concept living area and stunning mountain views.
              </ApartmentDescription>
              <ApartmentFeatures>
                <Feature>
                  <span>1739</span> sq ft
                </Feature>
                <Feature>
                  <span>2</span> Beds
                </Feature>
                <Feature>
                  <span>2</span> Baths
                </Feature>
              </ApartmentFeatures>
              <ViewButton to="/apartments">View Details</ViewButton>
            </ApartmentContent>
          </ApartmentCard>

          <ApartmentCard delay={0.4}>
            <ApartmentImage>
              <img src={threeBedroom} alt="3 Bedroom Apartment" />
            </ApartmentImage>
            <ApartmentContent>
              <ApartmentTitle>3 Bedroom Apartment</ApartmentTitle>
              <ApartmentDescription>
                Experience luxury living in our expansive 3-bedroom apartments,
                complete with premium finishes and panoramic views.
              </ApartmentDescription>
              <ApartmentFeatures>
                <Feature>
                  <span>2072</span> sq ft
                </Feature>
                <Feature>
                  <span>3</span> Beds
                </Feature>
                <Feature>
                  <span>3</span> Baths
                </Feature>
              </ApartmentFeatures>
              <ViewButton to="/apartments">View Details</ViewButton>
            </ApartmentContent>
          </ApartmentCard>
        </ApartmentGrid>
      </Container>
    </ApartmentsSection>
  );
};

export default Apartments;
